import {
  type CubeRun,
  calculateCubeScore,
  groupConsecutiveCubes,
  type MinusAuctionAction,
  type MinusAuctionViewerPlayer,
  type MinusAuctionViewerState,
} from "@cloudnest/minus-auction-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import {
  minusAuctionMultiplayerActions,
  useMinusAuctionMultiplayer,
  useMinusAuctionMultiplayerActions,
} from "./multiplayer-game-store";

type PlayerNameMap = Readonly<Record<string, string>>;
type MainTab = "GAME" | "TRADE" | "HIDDEN";
type ViewerActionValidation = {
  readonly valid: boolean;
  readonly error?: string;
};

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, myParticipantId, lastError } =
    useMinusAuctionMultiplayer();
  const actions = useMinusAuctionMultiplayerActions();
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<MainTab>("GAME");
  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferAmount, setTransferAmount] = useState(1);

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const isRoomObserver = Boolean(
    user?.id &&
      currentRoom?.observerUserIds?.includes(user.id) &&
      !(currentRoom.participantUserIds ?? []).includes(user.id),
  );
  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [actions, lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq) => {
      actionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void minusAuctionMultiplayerActions.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialize only when entering a concrete room/user role
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;

    if (isHost) {
      const playerIds = currentRoom.participantUserIds ?? [];
      if (playerIds.length === 0) return;
      void minusAuctionMultiplayerActions.initializeAsHost(
        roomId,
        playerIds,
        currentRoom.hostUserId,
        currentRoom.config,
      );
    } else if (isRoomObserver) {
      void minusAuctionMultiplayerActions.initializeAsObserver(roomId);
    } else {
      void minusAuctionMultiplayerActions.initializeAsGuest(roomId);
      void minusAuctionMultiplayerActions.replayEvents(roomId);
    }
  }, [roomId, currentRoom?.id, user?.id, isHost, isRoomObserver]);

  useEffect(() => {
    if (!roomId) return;
    const id = setInterval(() => {
      void minusAuctionMultiplayerActions.catchUpEvents(roomId);
    }, 4000);
    return () => clearInterval(id);
  }, [roomId]);

  const playerNames = useMemo<PlayerNameMap>(() => {
    if (!currentRoom) return {};
    return Object.fromEntries(
      (currentRoom.participantUserIds ?? []).map((playerId, index) => [
        playerId,
        getParticipantDisplayName(currentRoom, playerId, `Player ${index + 1}`),
      ]),
    );
  }, [currentRoom]);

  const transferReceiverFallback = useMemo(
    () =>
      gameState?.playerOrder.find((playerId) => playerId !== myParticipantId) ??
      "",
    [gameState?.playerOrder, myParticipantId],
  );

  useEffect(() => {
    const isHiddenPhase =
      gameState?.phase === "HIDDEN_AUCTION_BIDDING" ||
      gameState?.phase === "HIDDEN_AUCTION_DECISION";

    if (isHiddenPhase) {
      setSelectedTab((tab) => (tab === "HIDDEN" ? tab : "HIDDEN"));
      return;
    }

    setSelectedTab((tab) => (tab === "HIDDEN" ? "GAME" : tab));
  }, [gameState?.phase]);

  useEffect(() => {
    setTransferTargetId((currentTargetId) => {
      if (!gameState || !myParticipantId) {
        return transferReceiverFallback;
      }
      if (
        currentTargetId &&
        currentTargetId !== myParticipantId &&
        gameState.playerOrder.includes(currentTargetId)
      ) {
        return currentTargetId;
      }
      return transferReceiverFallback;
    });
  }, [gameState, myParticipantId, transferReceiverFallback]);

  function leaveGame(): void {
    actions.reset();
    void lobbyActions.leaveGame();
    navigateTo("/minus-auction/lobby");
  }

  if (!currentRoom) {
    return (
      <div className="ma-client-shell ma-client-center">
        <section className="ma-client-card">
          <h1>마이너스 경매</h1>
          <p>게임 방을 불러오는 중입니다.</p>
        </section>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="ma-client-shell ma-client-center">
        <section className="ma-client-card">
          <p className="ma-client-kicker">loading game</p>
          <h1>마이너스 경매</h1>
          <p>
            {connectionStatus === "connected"
              ? "방장이 게임 상태를 준비 중입니다."
              : "게임 서버에 연결 중입니다."}
          </p>
          {lastError ? (
            <div className="ma-client-error">{lastError}</div>
          ) : null}
        </section>
      </div>
    );
  }

  const detailPlayer = detailPlayerId
    ? (gameState.players.find((player) => player.playerId === detailPlayerId) ??
      null)
    : null;
  const isHiddenPhase =
    gameState.phase === "HIDDEN_AUCTION_BIDDING" ||
    gameState.phase === "HIDDEN_AUCTION_DECISION";
  const activeTab =
    selectedTab === "HIDDEN" && !isHiddenPhase ? "GAME" : selectedTab;

  return (
    <div className="ma-client-shell" data-testid="minus-auction-client-screen">
      <RoundTopBar
        connectionStatus={connectionStatus}
        nameByPlayerId={playerNames}
        onLeave={leaveGame}
        state={gameState}
      />

      {lastError ? (
        <div className="ma-client-error ma-client-floating-error">
          <span>{lastError}</span>
          <button type="button" onClick={() => actions.clearError()}>
            닫기
          </button>
        </div>
      ) : null}

      {gameState.phase === "GAME_OVER" ? (
        <GameOverPanel nameByPlayerId={playerNames} state={gameState} />
      ) : activeTab === "HIDDEN" && isHiddenPhase ? (
        <HiddenPhaseMain
          myParticipantId={myParticipantId}
          nameByPlayerId={playerNames}
          state={gameState}
        />
      ) : activeTab === "TRADE" ? (
        <GarnetTradeMain
          myParticipantId={myParticipantId}
          nameByPlayerId={playerNames}
          onPlayerSelect={setTransferTargetId}
          selectedPlayerId={transferTargetId}
          state={gameState}
        />
      ) : (
        <GameMain
          myParticipantId={myParticipantId}
          nameByPlayerId={playerNames}
          onPlayerSelect={setDetailPlayerId}
          state={gameState}
        />
      )}

      {gameState.phase !== "GAME_OVER" ? (
        <MatchActionDock
          hiddenTabEnabled={isHiddenPhase}
          myParticipantId={myParticipantId}
          nameByPlayerId={playerNames}
          onTabChange={setSelectedTab}
          onTransferAmountChange={setTransferAmount}
          onTransferTargetChange={setTransferTargetId}
          selectedTab={activeTab}
          state={gameState}
          transferAmount={transferAmount}
          transferTargetId={transferTargetId}
        />
      ) : null}

      {detailPlayer ? (
        <PlayerCubeDetailSheet
          name={getPlayerName(playerNames, detailPlayer.playerId)}
          onClose={() => setDetailPlayerId(null)}
          player={detailPlayer}
        />
      ) : null}
    </div>
  );
}

function RoundTopBar({
  connectionStatus,
  nameByPlayerId,
  onLeave,
  state,
}: {
  readonly connectionStatus: string;
  readonly nameByPlayerId: PlayerNameMap;
  readonly onLeave: () => void;
  readonly state: MinusAuctionViewerState;
}) {
  const auctionNumber =
    state.phase === "GAME_OVER"
      ? state.acceptedCubeCount
      : state.acceptedCubeCount + (state.auction ? 1 : 0);
  const auctionTotal =
    state.hiddenAuctionResolved && !state.secondHiddenCubeIncluded ? 31 : 32;

  return (
    <header className="ma-client-topbar" data-testid="minus-auction-topbar">
      <div className="ma-client-round">
        <strong>
          경매 {auctionNumber}/{auctionTotal}
        </strong>
        <span>
          {getPhaseLabel(state)} · {connectionStatus}
        </span>
      </div>

      {state.auction ? (
        <div
          className="ma-client-current-cube"
          data-testid="minus-auction-current-cube"
        >
          <Cube cube={state.auction.currentCube} size="large" />
          <div>
            <span className="ma-client-pile-line chip">
              <i aria-hidden="true" />칩 {state.auction.pileChips}
            </span>
            <span className="ma-client-pile-line garnet">
              <i aria-hidden="true" />G {state.auction.pileGarnets}
            </span>
          </div>
        </div>
      ) : null}

      <div className="ma-client-top-actions">
        <span>
          시작 {getPlayerName(nameByPlayerId, state.auction?.startPlayerId)}
        </span>
        <button type="button" onClick={onLeave}>
          나가기
        </button>
      </div>
    </header>
  );
}

function GameMain({
  myParticipantId,
  nameByPlayerId,
  onPlayerSelect,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly onPlayerSelect: (playerId: string) => void;
  readonly state: MinusAuctionViewerState;
}) {
  return (
    <div className="ma-client-main">
      <section className="ma-client-board" aria-label="플레이어 타일 현황">
        <PlayerQueue
          activePlayerId={getActivePlayerId(state)}
          myParticipantId={myParticipantId}
          nameByPlayerId={nameByPlayerId}
          onPlayerSelect={onPlayerSelect}
          state={state}
        />
      </section>

      <aside className="ma-client-side">
        {state.phase === "HIDDEN_AUCTION_BIDDING" ||
        state.phase === "HIDDEN_AUCTION_DECISION" ? (
          <HiddenBidderStatusList
            nameByPlayerId={nameByPlayerId}
            state={state}
          />
        ) : null}
        <LogPanel nameByPlayerId={nameByPlayerId} state={state} />
      </aside>
    </div>
  );
}

function GarnetTradeMain({
  myParticipantId,
  nameByPlayerId,
  onPlayerSelect,
  selectedPlayerId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly onPlayerSelect: (playerId: string) => void;
  readonly selectedPlayerId: string;
  readonly state: MinusAuctionViewerState;
}) {
  const activePlayerId = getActivePlayerId(state);
  const orderedPlayerIds = useMemo(
    () => rotatePlayerOrder(state.playerOrder, activePlayerId),
    [state.playerOrder, activePlayerId],
  );

  return (
    <div
      className="ma-client-main ma-client-trade-main"
      data-testid="minus-auction-trade-main"
    >
      <section className="ma-client-board" aria-label="플레이어 가넷 거래">
        <div
          className="ma-client-player-list"
          data-testid="minus-auction-trade-player-list"
        >
          {orderedPlayerIds.map((playerId) => {
            const player = getPlayer(state, playerId);
            return (
              <GarnetPlayerTile
                isActive={playerId === activePlayerId}
                isMe={playerId === myParticipantId}
                isSelected={playerId === selectedPlayerId}
                key={playerId}
                name={getPlayerName(nameByPlayerId, playerId)}
                onSelect={() => {
                  if (myParticipantId && playerId !== myParticipantId) {
                    onPlayerSelect(playerId);
                  }
                }}
                player={player}
                state={state}
              />
            );
          })}
        </div>
      </section>

      <aside className="ma-client-side">
        <LogPanel nameByPlayerId={nameByPlayerId} state={state} />
      </aside>
    </div>
  );
}

function GarnetPlayerTile({
  isActive,
  isMe,
  isSelected,
  name,
  onSelect,
  player,
  state,
}: {
  readonly isActive: boolean;
  readonly isMe: boolean;
  readonly isSelected: boolean;
  readonly name: string;
  readonly onSelect: () => void;
  readonly player: MinusAuctionViewerPlayer;
  readonly state: MinusAuctionViewerState;
}) {
  const hiddenLockedGarnets =
    state.hiddenAuction?.lockedGarnetsByPlayerId[player.playerId] ?? null;
  const lockedGarnets = player.lockedGarnets ?? hiddenLockedGarnets;
  const canSelect = !isMe;

  return (
    <button
      type="button"
      aria-label={`${name}에게 가넷 보내기`}
      aria-pressed={isSelected}
      className={`ma-client-player ma-client-garnet-player ${
        isActive ? "active" : ""
      } ${isSelected ? "selected" : ""} ${isMe ? "self" : ""}`}
      data-testid="minus-auction-trade-player"
      onClick={canSelect ? onSelect : undefined}
    >
      <div className="ma-client-player-id">
        <strong title={name}>{isMe ? `나 · ${name}` : name}</strong>
        <span>보유 가넷 {formatPrivateNumber(player.garnets)}G</span>
      </div>
      <div className="ma-client-garnet-balance">
        <span>G</span>
        <strong>{formatPrivateNumber(player.garnets)}</strong>
        <small>잠김 {formatPrivateNumber(lockedGarnets)}</small>
      </div>
    </button>
  );
}

function HiddenPhaseMain({
  myParticipantId,
  nameByPlayerId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  return (
    <main
      className="ma-client-hidden-main"
      data-testid="minus-auction-hidden-main"
    >
      <section className="ma-client-hidden-primary">
        <HiddenPhaseHeader
          myParticipantId={myParticipantId}
          nameByPlayerId={nameByPlayerId}
          state={state}
        />
        {state.phase === "HIDDEN_AUCTION_BIDDING" ? (
          <HiddenAuctionPanel
            myParticipantId={myParticipantId}
            nameByPlayerId={nameByPlayerId}
            state={state}
          />
        ) : null}
        {state.phase === "HIDDEN_AUCTION_DECISION" ? (
          <HiddenDecisionPanel
            myParticipantId={myParticipantId}
            nameByPlayerId={nameByPlayerId}
            state={state}
          />
        ) : null}
      </section>
      <aside className="ma-client-hidden-support">
        <HiddenBidderStatusList nameByPlayerId={nameByPlayerId} state={state} />
        <LogPanel nameByPlayerId={nameByPlayerId} state={state} />
      </aside>
    </main>
  );
}

function HiddenPhaseHeader({
  myParticipantId,
  nameByPlayerId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  const hiddenAuction = state.hiddenAuction;
  const isDecision = state.phase === "HIDDEN_AUCTION_DECISION";
  const actorId = isDecision
    ? hiddenAuction?.highestBidderId
    : hiddenAuction?.currentBidderId;
  const isMyAction = actorId === myParticipantId;
  const actorName = getPlayerName(nameByPlayerId, actorId);
  const modeLabel = isDecision ? "메인 · 결정" : "메인 · 입찰";
  const actionCopy = isDecision
    ? isMyAction
      ? "히든 타일을 남은 경매에 넣을지 선택하세요."
      : `${actorName}가 히든 타일을 확인 중입니다.`
    : isMyAction
      ? "입찰가를 올리거나 이번 히든 경매를 포기하세요."
      : `${actorName}의 입찰 선택을 기다리는 중입니다.`;

  return (
    <header
      className="ma-client-hidden-mode"
      data-testid="minus-auction-hidden-phase-header"
    >
      <div>
        <span>{modeLabel}</span>
        <strong>
          {isDecision ? "두 번째 히든 타일 결정" : "두 번째 히든 타일 입찰"}
        </strong>
      </div>
      <p>{actionCopy}</p>
    </header>
  );
}

function PlayerQueue({
  activePlayerId,
  myParticipantId,
  nameByPlayerId,
  onPlayerSelect,
  state,
}: {
  readonly activePlayerId: string | null;
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly onPlayerSelect: (playerId: string) => void;
  readonly state: MinusAuctionViewerState;
}) {
  const orderedPlayerIds = useMemo(
    () => rotatePlayerOrder(state.playerOrder, activePlayerId),
    [state.playerOrder, activePlayerId],
  );

  return (
    <div
      className="ma-client-player-list"
      data-testid="minus-auction-player-list"
    >
      {orderedPlayerIds.map((playerId) => {
        const player = getPlayer(state, playerId);
        return (
          <PlayerTile
            isActive={playerId === activePlayerId}
            isMe={playerId === myParticipantId}
            isStart={state.auction?.startPlayerId === playerId}
            key={playerId}
            name={getPlayerName(nameByPlayerId, playerId)}
            onSelect={() => onPlayerSelect(playerId)}
            player={player}
          />
        );
      })}
    </div>
  );
}

function PlayerTile({
  isActive,
  isMe,
  isStart,
  name,
  onSelect,
  player,
}: {
  readonly isActive: boolean;
  readonly isMe: boolean;
  readonly isStart: boolean;
  readonly name: string;
  readonly onSelect: () => void;
  readonly player: MinusAuctionViewerPlayer;
}) {
  const cubeScore = calculateCubeScore(player.cubes);
  const runs = groupConsecutiveCubes(player.cubes);

  return (
    <button
      type="button"
      aria-label={`${name} 보유 타일 상세 보기`}
      className={`ma-client-player ${isActive ? "active" : ""}`}
      data-testid="minus-auction-player"
      onClick={onSelect}
    >
      {isStart ? <span className="ma-client-start-badge">★</span> : null}
      <div className="ma-client-player-id">
        <strong title={name}>{isMe ? `나 · ${name}` : name}</strong>
        <span>{formatNumber(cubeScore.scoredCubeSum)}</span>
      </div>
      <div className="ma-client-rack">
        {runs.length > 0 ? (
          runs.map((run) => <CubeRunView key={run.cubes.join(",")} run={run} />)
        ) : (
          <span className="ma-client-empty">타일 없음</span>
        )}
      </div>
    </button>
  );
}

function CubeRunView({
  compact = true,
  run,
}: {
  readonly compact?: boolean;
  readonly run: CubeRun;
}) {
  const compactCubes =
    compact && run.cubes.length > 3
      ? [run.cubes[0], null, run.cubes.at(-1)]
      : run.cubes;
  const runSize = getCubeRunSize(run.cubes.length);

  return (
    <span className={`ma-client-run size-${runSize}`} data-run-size={runSize}>
      {compactCubes.map((cube, index) =>
        cube === null || cube === undefined ? (
          <span
            className="ma-client-cube ma-client-ellipsis muted"
            key="ellipsis"
          >
            ...
          </span>
        ) : (
          <Cube
            cube={cube}
            isMuted={cube !== run.scoredCube}
            key={`${cube}-${index}`}
            size="small"
          />
        ),
      )}
    </span>
  );
}

function PlayerCubeDetailSheet({
  name,
  onClose,
  player,
}: {
  readonly name: string;
  readonly onClose: () => void;
  readonly player: MinusAuctionViewerPlayer;
}) {
  const cubeScore = calculateCubeScore(player.cubes);

  return (
    <div
      className="ma-client-detail-scrim"
      data-testid="minus-auction-cube-detail-scrim"
      onClick={onClose}
    >
      <section
        aria-label={`${name} 보유 타일`}
        aria-modal="true"
        className="ma-client-cube-detail"
        data-testid="minus-auction-cube-detail"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="ma-client-detail-head">
          <div>
            <strong>{name} 보유 타일</strong>
            <span>
              전체 {player.cubes.length}개 · 계산 {cubeScore.scoredCubes.length}
              개 · {formatNumber(cubeScore.scoredCubeSum)}
            </span>
          </div>
          <button type="button" onClick={onClose}>
            닫기
          </button>
        </header>
        <div className="ma-client-detail-rack">
          {cubeScore.runs.length > 0 ? (
            cubeScore.runs.map((run) => (
              <CubeRunView
                compact={false}
                key={run.cubes.join(",")}
                run={run}
              />
            ))
          ) : (
            <span className="ma-client-empty">타일 없음</span>
          )}
        </div>
      </section>
    </div>
  );
}

function getCubeRunSize(cubeCount: number): "1" | "2" | "3" | "many" {
  if (cubeCount <= 1) {
    return "1";
  }
  if (cubeCount === 2) {
    return "2";
  }
  if (cubeCount === 3) {
    return "3";
  }
  return "many";
}

function Cube({
  cube,
  isMuted = false,
  size = "small",
}: {
  readonly cube: number;
  readonly isMuted?: boolean;
  readonly size?: "small" | "large";
}) {
  return (
    <span
      className={`ma-client-cube ${getCubeRisk(cube)} ${size} ${
        isMuted ? "muted" : ""
      }`}
      data-testid="minus-auction-cube"
      title={`${cube}`}
    >
      <span className="ma-client-cube-value">{cube}</span>
    </span>
  );
}

function MatchActionDock({
  hiddenTabEnabled,
  myParticipantId,
  nameByPlayerId,
  onTabChange,
  onTransferAmountChange,
  onTransferTargetChange,
  selectedTab,
  state,
  transferAmount,
  transferTargetId,
}: {
  readonly hiddenTabEnabled: boolean;
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly onTabChange: (tab: MainTab) => void;
  readonly onTransferAmountChange: (amount: number) => void;
  readonly onTransferTargetChange: (playerId: string) => void;
  readonly selectedTab: MainTab;
  readonly state: MinusAuctionViewerState;
  readonly transferAmount: number;
  readonly transferTargetId: string;
}) {
  return (
    <div
      className="ma-client-action-dock"
      data-testid="minus-auction-action-dock"
    >
      <div
        className="ma-client-view-tabs"
        role="tablist"
        aria-label="게임 화면"
      >
        <DockTab
          isSelected={selectedTab === "GAME"}
          label="인게임"
          testId="minus-auction-tab-game"
          onClick={() => onTabChange("GAME")}
        />
        <DockTab
          isSelected={selectedTab === "TRADE"}
          label="가넷 거래"
          testId="minus-auction-tab-trade"
          onClick={() => onTabChange("TRADE")}
        />
        <DockTab
          disabled={!hiddenTabEnabled}
          isSelected={selectedTab === "HIDDEN"}
          label="히든 경매"
          testId="minus-auction-tab-hidden"
          onClick={() => onTabChange("HIDDEN")}
        />
      </div>

      <MyStatus myParticipantId={myParticipantId} state={state} />

      <div className="ma-client-dock-body">
        {selectedTab === "TRADE" ? (
          <TransferDockActions
            amount={transferAmount}
            myParticipantId={myParticipantId}
            nameByPlayerId={nameByPlayerId}
            onAmountChange={onTransferAmountChange}
            onTargetChange={onTransferTargetChange}
            state={state}
            targetPlayerId={transferTargetId}
          />
        ) : selectedTab === "HIDDEN" ? (
          <HiddenDockActions
            myParticipantId={myParticipantId}
            nameByPlayerId={nameByPlayerId}
            state={state}
          />
        ) : (
          <AuctionDockActions myParticipantId={myParticipantId} state={state} />
        )}
      </div>
    </div>
  );
}

function DockTab({
  disabled = false,
  isSelected,
  label,
  onClick,
  testId,
}: {
  readonly disabled?: boolean;
  readonly isSelected: boolean;
  readonly label: string;
  readonly onClick: () => void;
  readonly testId: string;
}) {
  return (
    <button
      type="button"
      aria-selected={isSelected}
      className={`ma-client-view-tab ${isSelected ? "selected" : ""}`}
      data-testid={testId}
      disabled={disabled}
      role="tab"
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function MyStatus({
  myParticipantId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly state: MinusAuctionViewerState;
}) {
  const player = myParticipantId
    ? (state.players.find(
        (candidate) => candidate.playerId === myParticipantId,
      ) ?? null)
    : null;
  const score = player ? calculateCubeScore(player.cubes).scoredCubeSum : null;

  return (
    <div className="ma-client-my-status" data-testid="minus-auction-my-status">
      <span className="ma-client-my-stat" data-testid="minus-auction-my-score">
        <small>내 점수</small>
        <strong>{score === null ? "관전" : formatNumber(score)}</strong>
      </span>
      <span className="ma-client-my-stat" data-testid="minus-auction-my-chips">
        <small>칩</small>
        <strong>{formatPrivateNumber(player?.chips)}</strong>
      </span>
      <span
        className="ma-client-my-stat garnet"
        data-testid="minus-auction-my-garnets"
      >
        <small>가넷</small>
        <strong>{formatPrivateNumber(player?.garnets)}</strong>
      </span>
    </div>
  );
}

function AuctionDockActions({
  myParticipantId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly state: MinusAuctionViewerState;
}) {
  if (!myParticipantId) {
    return <DockNote>관전 중입니다.</DockNote>;
  }
  if (!state.auction || state.phase !== "AUCTION_DECISION") {
    return <DockNote>현재 경매 선택을 기다리는 중입니다.</DockNote>;
  }

  const actions: readonly {
    readonly label: string;
    readonly primary?: boolean;
    readonly action: MinusAuctionAction;
  }[] = [
    {
      label: "거부 (가넷)",
      action: {
        type: "RejectCube",
        playerId: myParticipantId,
        resource: "GARNET",
      },
    },
    {
      label: "거부 (칩)",
      action: {
        type: "RejectCube",
        playerId: myParticipantId,
        resource: "CHIP",
      },
    },
    {
      label: "낙찰 받기",
      primary: true,
      action: { type: "TakeCube", playerId: myParticipantId },
    },
  ];

  return (
    <div className="ma-client-auction-actions">
      {actions.map(({ action, label, primary }) => (
        <SubmitButton
          action={action}
          key={label}
          label={label}
          primary={primary}
          state={state}
        />
      ))}
    </div>
  );
}

function DockNote({ children }: { readonly children: string }) {
  return <div className="ma-client-dock-note">{children}</div>;
}

function SubmitButton({
  action,
  label,
  primary = false,
  state,
}: {
  readonly action: MinusAuctionAction;
  readonly label: string;
  readonly primary?: boolean;
  readonly state: MinusAuctionViewerState;
}) {
  const validation = validateViewerAction(state, action);
  return (
    <button
      type="button"
      className={`ma-client-button ${primary ? "primary" : ""}`}
      disabled={!validation.valid}
      title={validation.valid ? label : validation.error}
      onClick={() => void minusAuctionMultiplayerActions.submitAction(action)}
    >
      {label}
    </button>
  );
}

function TransferDockActions({
  amount,
  myParticipantId,
  nameByPlayerId,
  onAmountChange,
  onTargetChange,
  state,
  targetPlayerId,
}: {
  readonly amount: number;
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly onAmountChange: (amount: number) => void;
  readonly onTargetChange: (playerId: string) => void;
  readonly state: MinusAuctionViewerState;
  readonly targetPlayerId: string;
}) {
  if (!myParticipantId) {
    return <DockNote>관전자는 가넷을 보낼 수 없습니다.</DockNote>;
  }
  if (
    state.phase !== "AUCTION_DECISION" &&
    state.phase !== "HIDDEN_AUCTION_BIDDING"
  ) {
    return (
      <DockNote>
        경매 선택 또는 히든 입찰 중에 가넷을 보낼 수 있습니다.
      </DockNote>
    );
  }

  const transferable = getTransferableGarnets(state, myParticipantId);
  const receiverFallback =
    state.playerOrder.find((playerId) => playerId !== myParticipantId) ?? "";
  const toPlayerId =
    targetPlayerId && targetPlayerId !== myParticipantId
      ? targetPlayerId
      : receiverFallback;
  const safeAmount = Math.max(1, Math.trunc(amount));
  const action: MinusAuctionAction = {
    type: "TransferGarnet",
    fromPlayerId: myParticipantId,
    toPlayerId,
    amount: safeAmount,
  };
  const validation = validateViewerAction(state, action);

  return (
    <div
      className="ma-client-transfer-actions"
      data-testid="minus-auction-transfer-controls"
    >
      <label className="ma-client-transfer-target">
        <span>대상</span>
        <select
          data-testid="minus-auction-transfer-target"
          value={toPlayerId}
          onChange={(event) => onTargetChange(event.currentTarget.value)}
        >
          {state.playerOrder
            .filter((playerId) => playerId !== myParticipantId)
            .map((playerId) => (
              <option key={playerId} value={playerId}>
                {getPlayerName(nameByPlayerId, playerId)}에게
              </option>
            ))}
        </select>
      </label>
      <label className="ma-client-transfer-amount">
        <span>수량</span>
        <input
          data-testid="minus-auction-transfer-amount"
          max={Math.max(1, transferable)}
          min={1}
          type="number"
          value={safeAmount}
          onChange={(event) =>
            onAmountChange(Number(event.currentTarget.value))
          }
        />
      </label>
      <button
        type="button"
        className="ma-client-button primary"
        data-testid="minus-auction-send-garnet"
        disabled={!validation.valid}
        title={validation.valid ? "가넷 보내기" : validation.error}
        onClick={() => void minusAuctionMultiplayerActions.submitAction(action)}
      >
        보내기
      </button>
    </div>
  );
}

function HiddenBidderStatusList({
  nameByPlayerId,
  state,
}: {
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  const hiddenAuction = state.hiddenAuction;
  if (!hiddenAuction) {
    return null;
  }

  return (
    <section
      className="ma-client-panel ma-client-hidden-bidders"
      aria-label="히든 타일 입찰 현황"
      data-testid="minus-auction-hidden-bidders"
    >
      <div className="ma-client-panel-head">
        <strong>입찰 현황</strong>
        <span>마지막 입찰가 {hiddenAuction.currentBid}G</span>
      </div>
      <div className="ma-client-bidder-list">
        {state.playerOrder.map((playerId) => {
          const isCurrent = hiddenAuction.currentBidderId === playerId;
          const isHighest = hiddenAuction.highestBidderId === playerId;
          const isOut = hiddenAuction.passedPlayerIds.includes(playerId);
          const lastBid = hiddenAuction.lastBidByPlayerId[playerId] ?? 0;

          return (
            <div
              className={`ma-client-bidder-tile ${
                isCurrent ? "current" : ""
              } ${isHighest ? "highest" : ""} ${isOut ? "out" : ""}`}
              key={playerId}
              style={{
                borderLeftColor: getSeatColor(state.playerOrder, playerId),
              }}
            >
              <strong>{getPlayerName(nameByPlayerId, playerId)}</strong>
              <span>{lastBid}G</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function HiddenDockActions({
  myParticipantId,
  nameByPlayerId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  if (state.phase === "HIDDEN_AUCTION_BIDDING") {
    return (
      <HiddenBidControls
        myParticipantId={myParticipantId}
        nameByPlayerId={nameByPlayerId}
        state={state}
      />
    );
  }

  if (state.phase === "HIDDEN_AUCTION_DECISION") {
    return (
      <HiddenDecisionDockActions
        myParticipantId={myParticipantId}
        state={state}
      />
    );
  }

  return <DockNote>17번째 낙찰 후 히든 경매가 열립니다.</DockNote>;
}

function HiddenBidControls({
  myParticipantId,
  nameByPlayerId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  const hiddenAuction = state.hiddenAuction;
  const defaultBidAmount =
    hiddenAuction && hiddenAuction.currentBidderId === myParticipantId
      ? hiddenAuction.currentBid + 1
      : (hiddenAuction?.currentBid ?? 0);
  const [bidAmount, setBidAmount] = useState(defaultBidAmount);

  useEffect(() => {
    setBidAmount(defaultBidAmount);
  }, [defaultBidAmount]);

  if (!hiddenAuction?.currentBidderId) {
    return <DockNote>히든 경매가 완료되었습니다.</DockNote>;
  }
  if (!myParticipantId) {
    return <DockNote>관전 중입니다.</DockNote>;
  }

  const currentBidderId = hiddenAuction.currentBidderId;
  const currentBid = hiddenAuction.currentBid;
  const minimumBid = currentBid + 1;
  const availableGarnets = getTransferableGarnets(state, myParticipantId);
  const safeBidAmount = Math.max(0, Math.trunc(bidAmount));
  const bidAction: MinusAuctionAction = {
    type: "BidHiddenCube",
    playerId: myParticipantId,
    amount: safeBidAmount,
  };
  const bidValidation = validateViewerAction(state, bidAction);
  const canStepDown = safeBidAmount > minimumBid;
  const canStepUp = safeBidAmount < availableGarnets;
  const passAction: MinusAuctionAction = {
    type: "PassHiddenCubeBid",
    playerId: myParticipantId,
  };
  const passValidation = validateViewerAction(state, passAction);
  const isMyBidTurn = currentBidderId === myParticipantId;

  return (
    <div
      className="ma-client-bid-action-stack"
      data-testid="minus-auction-hidden-bid-actions"
    >
      <div className="ma-client-bid-control-row">
        <div className="ma-client-last-bid">
          <span>마지막 입찰가</span>
          <strong>{currentBid}G</strong>
        </div>
        <div className="ma-client-bid-controls">
          <button
            type="button"
            className="ma-client-step-button"
            disabled={safeBidAmount === currentBid}
            onClick={() => setBidAmount(currentBid)}
          >
            초기화
          </button>
          <button
            type="button"
            className="ma-client-step-button"
            disabled={!isMyBidTurn || !canStepDown}
            onClick={() =>
              setBidAmount((amount) => Math.max(minimumBid, amount - 1))
            }
          >
            -1
          </button>
          <button
            type="button"
            className="ma-client-step-button"
            disabled={!isMyBidTurn || !canStepUp}
            onClick={() => setBidAmount((amount) => amount + 1)}
          >
            +1
          </button>
        </div>
        <div className="ma-client-my-bid">
          <span>내 입찰가</span>
          <strong>{safeBidAmount}G</strong>
        </div>
      </div>
      <div className="ma-client-bid-decision-row">
        <button
          type="button"
          className="ma-client-button"
          disabled={!passValidation.valid}
          title={passValidation.valid ? "포기" : passValidation.error}
          onClick={() =>
            void minusAuctionMultiplayerActions.submitAction(passAction)
          }
        >
          포기
        </button>
        <button
          type="button"
          className="ma-client-button primary"
          disabled={!bidValidation.valid || safeBidAmount <= currentBid}
          title={bidValidation.valid ? "입찰" : bidValidation.error}
          onClick={() =>
            void minusAuctionMultiplayerActions.submitAction(bidAction)
          }
        >
          입찰
        </button>
      </div>
      {!isMyBidTurn ? (
        <p className="ma-client-hidden-wait">
          {getPlayerName(nameByPlayerId, currentBidderId)}가 입찰하거나 포기할
          때까지 기다립니다.
        </p>
      ) : null}
    </div>
  );
}

function HiddenDecisionDockActions({
  myParticipantId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly state: MinusAuctionViewerState;
}) {
  const hiddenAuction = state.hiddenAuction;
  const winnerId = hiddenAuction?.highestBidderId;

  if (!myParticipantId || winnerId !== myParticipantId) {
    return <DockNote>낙찰자의 히든 타일 결정을 기다리는 중입니다.</DockNote>;
  }

  const includeAction: MinusAuctionAction = {
    type: "DecideHiddenCube",
    playerId: myParticipantId,
    decision: "INCLUDE",
  };
  const excludeAction: MinusAuctionAction = {
    type: "DecideHiddenCube",
    playerId: myParticipantId,
    decision: "EXCLUDE",
  };

  return (
    <div
      className="ma-client-decision-row"
      data-testid="minus-auction-hidden-decision-actions"
    >
      <SubmitButton
        action={includeAction}
        label="경매에 포함"
        primary
        state={state}
      />
      <SubmitButton action={excludeAction} label="비공개 제외" state={state} />
    </div>
  );
}

function HiddenAuctionPanel({
  myParticipantId,
  nameByPlayerId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  const hiddenAuction = state.hiddenAuction;

  if (!hiddenAuction?.currentBidderId) {
    return null;
  }

  const currentBidderId = hiddenAuction.currentBidderId;
  const currentBid = hiddenAuction.currentBid;
  const minimumBid = currentBid + 1;
  const isMyBidTurn = currentBidderId === myParticipantId;

  return (
    <section
      className="ma-client-panel ma-client-hidden-auction-panel"
      aria-label="히든 타일 입찰"
      data-testid="minus-auction-hidden-bidding-panel"
    >
      <div className="ma-client-panel-head">
        <strong>히든 타일 입찰</strong>
        <span>{getPlayerName(nameByPlayerId, currentBidderId)}</span>
      </div>
      <div className="ma-client-hidden-status">
        <span>공개 순차 입찰</span>
        <span>
          {isMyBidTurn
            ? `내 차례 · 최소 ${minimumBid}G`
            : `${getPlayerName(nameByPlayerId, currentBidderId)} 차례`}
        </span>
      </div>
      <div className="ma-client-hidden-panel-note">
        <span>마지막 입찰가</span>
        <strong>{currentBid}G</strong>
      </div>
    </section>
  );
}

export function HiddenDecisionPanel({
  myParticipantId,
  nameByPlayerId,
  state,
}: {
  readonly myParticipantId: string | null;
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  const hiddenAuction = state.hiddenAuction;
  const winnerId = hiddenAuction?.highestBidderId;
  if (!hiddenAuction || !winnerId) {
    return null;
  }

  if (winnerId !== myParticipantId) {
    return (
      <section
        className="ma-client-panel ma-client-hidden-waiting-panel"
        aria-label="히든 타일 확인 대기"
        data-testid="minus-auction-hidden-decision-waiting"
      >
        <div className="ma-client-panel-head">
          <strong>히든 타일 확인 중</strong>
          <span>{getPlayerName(nameByPlayerId, winnerId)}</span>
        </div>
        <p>
          낙찰자만 히든 타일 값을 볼 수 있습니다. 결정 후에는 포함/제외 여부만
          공개됩니다.
        </p>
      </section>
    );
  }

  return (
    <section
      className="ma-client-panel"
      aria-label="히든 타일 결정"
      data-testid="minus-auction-hidden-decision-panel"
    >
      <div className="ma-client-panel-head">
        <strong>히든 타일 결정</strong>
        <span>{getPlayerName(nameByPlayerId, winnerId)}</span>
      </div>
      <div className="ma-client-hidden-cube">
        {hiddenAuction.cube === null ? (
          <span
            className="ma-client-cube high large muted"
            data-testid="minus-auction-hidden-cube-redacted"
            title="hidden"
          >
            <span className="ma-client-cube-value">?</span>
          </span>
        ) : (
          <Cube cube={hiddenAuction.cube} size="large" />
        )}
        <span>{hiddenAuction.currentBid}G 낙찰</span>
      </div>
    </section>
  );
}

function LogPanel({
  nameByPlayerId,
  state,
}: {
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  return (
    <section
      className="ma-client-panel ma-client-log"
      aria-label="최근 기록"
      data-testid="minus-auction-log-panel"
    >
      <div className="ma-client-panel-head">
        <strong>최근 기록</strong>
        <span>{state.auctionLog.length}</span>
      </div>
      <ol>
        {state.auctionLog
          .slice(-5)
          .reverse()
          .map((entry, index) => (
            <li key={`${entry.message}-${index}`}>
              <span>{entry.message}</span>
              {entry.playerId ? (
                <small>{getPlayerName(nameByPlayerId, entry.playerId)}</small>
              ) : null}
            </li>
          ))}
      </ol>
    </section>
  );
}

function GameOverPanel({
  nameByPlayerId,
  state,
}: {
  readonly nameByPlayerId: PlayerNameMap;
  readonly state: MinusAuctionViewerState;
}) {
  return (
    <section className="ma-client-results">
      <p className="ma-client-kicker">lowest score wins</p>
      <h1>게임 종료</h1>
      <div className="ma-client-score-table">
        {state.scores.map((score, index) => (
          <article className="ma-client-score-row" key={score.playerId}>
            <strong>{index + 1}</strong>
            <div>
              <b>{getPlayerName(nameByPlayerId, score.playerId)}</b>
              <span>
                타일 {score.scoredCubeSum} · 칩 차감 {score.remainingChips} ·
                {score.chipSurplus > 0 ? ` 칩 여유 ${score.chipSurplus} ·` : ""}{" "}
                가넷 {score.remainingGarnets}
              </span>
            </div>
            <strong>{formatNumber(score.finalScore)}</strong>
            {state.winnerIds.includes(score.playerId) ? (
              <span className="ma-client-score-tag winner">우승</span>
            ) : null}
            {state.eliminationCandidateIds.includes(score.playerId) ? (
              <span className="ma-client-score-tag danger">탈락 후보</span>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function getPlayer(
  state: MinusAuctionViewerState,
  playerId: string,
): MinusAuctionViewerPlayer {
  const player = state.players.find(
    (candidate) => candidate.playerId === playerId,
  );
  if (!player) {
    throw new Error(`Unknown player: ${playerId}`);
  }
  return player;
}

function getActivePlayerId(state: MinusAuctionViewerState): string | null {
  if (state.phase === "AUCTION_DECISION") {
    return state.auction?.currentPlayerId ?? null;
  }
  if (state.phase === "HIDDEN_AUCTION_BIDDING") {
    return state.hiddenAuction?.currentBidderId ?? null;
  }
  if (state.phase === "HIDDEN_AUCTION_DECISION") {
    return state.hiddenAuction?.highestBidderId ?? null;
  }
  return null;
}

function getPlayerName(
  nameByPlayerId: PlayerNameMap,
  playerId: string | null | undefined,
): string {
  if (!playerId) {
    return "없음";
  }
  return nameByPlayerId[playerId] ?? playerId;
}

function getTransferableGarnets(
  state: MinusAuctionViewerState,
  playerId: string,
): number {
  const player = state.players.find(
    (candidate) => candidate.playerId === playerId,
  );
  if (!player) {
    return 0;
  }
  if (typeof player.availableGarnets === "number") {
    return Math.max(0, player.availableGarnets);
  }
  const locked = player.lockedGarnets ?? 0;
  return typeof player.garnets === "number"
    ? Math.max(0, player.garnets - locked)
    : 0;
}

function validateViewerAction(
  state: MinusAuctionViewerState,
  action: MinusAuctionAction,
): ViewerActionValidation {
  if (action.type === "RejectCube") {
    const actor = validateViewerAuctionActor(state, action.playerId);
    if (!actor.valid) {
      return actor;
    }
    const player = findViewerPlayer(state, action.playerId);
    if (!player) {
      return { valid: false, error: "Unknown player" };
    }
    if (action.resource === "CHIP") {
      return typeof player.chips === "number" && player.chips > 0
        ? { valid: true }
        : { valid: false, error: "No chips available" };
    }
    if (player.chips !== 0) {
      return {
        valid: false,
        error: "Garnets can only be used when the player has no chips",
      };
    }
    return typeof player.garnets === "number" && player.garnets > 0
      ? { valid: true }
      : { valid: false, error: "No garnets available" };
  }

  if (action.type === "TakeCube") {
    return validateViewerAuctionActor(state, action.playerId);
  }

  if (action.type === "TransferGarnet") {
    if (
      state.phase !== "AUCTION_DECISION" &&
      state.phase !== "HIDDEN_AUCTION_BIDDING"
    ) {
      return { valid: false, error: "Garnets cannot be transferred now" };
    }
    if (state.viewerPlayerId !== action.fromPlayerId) {
      return { valid: false, error: "Only your own garnets can be sent" };
    }
    if (action.fromPlayerId === action.toPlayerId) {
      return { valid: false, error: "Cannot transfer garnets to self" };
    }
    if (!Number.isInteger(action.amount) || action.amount < 1) {
      return {
        valid: false,
        error: "Transfer amount must be a positive integer",
      };
    }
    if (
      !state.playerOrder.includes(action.fromPlayerId) ||
      !state.playerOrder.includes(action.toPlayerId)
    ) {
      return { valid: false, error: "Unknown transfer player" };
    }
    return getTransferableGarnets(state, action.fromPlayerId) >= action.amount
      ? { valid: true }
      : { valid: false, error: "Not enough transferable garnets" };
  }

  if (action.type === "BidHiddenCube") {
    const hiddenAuction = state.hiddenAuction;
    if (state.phase !== "HIDDEN_AUCTION_BIDDING" || !hiddenAuction) {
      return { valid: false, error: "Hidden tile bidding is not active" };
    }
    if (state.viewerPlayerId !== action.playerId) {
      return { valid: false, error: "Only your own bid can be submitted" };
    }
    if (hiddenAuction.currentBidderId !== action.playerId) {
      return { valid: false, error: "It is not this player's hidden bid turn" };
    }
    if (hiddenAuction.passedPlayerIds.includes(action.playerId)) {
      return {
        valid: false,
        error: "Player already passed this hidden auction",
      };
    }
    if (
      !Number.isInteger(action.amount) ||
      action.amount <= hiddenAuction.currentBid
    ) {
      return {
        valid: false,
        error: "Bid must be at least one garnet higher than the current bid",
      };
    }
    return getTransferableGarnets(state, action.playerId) >= action.amount
      ? { valid: true }
      : { valid: false, error: "Not enough available garnets for bid" };
  }

  if (action.type === "PassHiddenCubeBid") {
    const hiddenAuction = state.hiddenAuction;
    if (state.phase !== "HIDDEN_AUCTION_BIDDING" || !hiddenAuction) {
      return { valid: false, error: "Hidden tile bidding is not active" };
    }
    if (state.viewerPlayerId !== action.playerId) {
      return { valid: false, error: "Only your own pass can be submitted" };
    }
    if (hiddenAuction.currentBidderId !== action.playerId) {
      return { valid: false, error: "It is not this player's hidden bid turn" };
    }
    if (hiddenAuction.passedPlayerIds.includes(action.playerId)) {
      return {
        valid: false,
        error: "Player already passed this hidden auction",
      };
    }
    return { valid: true };
  }

  if (action.type === "DecideHiddenCube") {
    const hiddenAuction = state.hiddenAuction;
    if (state.phase !== "HIDDEN_AUCTION_DECISION" || !hiddenAuction) {
      return { valid: false, error: "Hidden tile decision is not active" };
    }
    if (state.viewerPlayerId !== action.playerId) {
      return { valid: false, error: "Only your own decision can be submitted" };
    }
    if (hiddenAuction.highestBidderId !== action.playerId) {
      return { valid: false, error: "Only the hidden tile winner can decide" };
    }
    if (hiddenAuction.cube === null) {
      return { valid: false, error: "Hidden tile is not visible yet" };
    }
    return { valid: true };
  }

  return { valid: false, error: "Unknown action" };
}

function validateViewerAuctionActor(
  state: MinusAuctionViewerState,
  playerId: string,
): ViewerActionValidation {
  if (state.viewerPlayerId !== playerId) {
    return { valid: false, error: "Only your own action can be submitted" };
  }
  if (state.phase !== "AUCTION_DECISION" || !state.auction) {
    return { valid: false, error: "Auction decision is not active" };
  }
  if (state.auction.currentPlayerId !== playerId) {
    return { valid: false, error: "It is not this player's auction turn" };
  }
  return { valid: true };
}

function findViewerPlayer(
  state: MinusAuctionViewerState,
  playerId: string,
): MinusAuctionViewerPlayer | null {
  return (
    state.players.find((candidate) => candidate.playerId === playerId) ?? null
  );
}

function rotatePlayerOrder(
  playerOrder: readonly string[],
  activePlayerId: string | null,
): readonly string[] {
  if (!activePlayerId) {
    return playerOrder;
  }
  const activeIndex = playerOrder.indexOf(activePlayerId);
  if (activeIndex < 0) {
    return playerOrder;
  }
  return [
    ...playerOrder.slice(activeIndex),
    ...playerOrder.slice(0, activeIndex),
  ];
}

const SEAT_COLORS = [
  "#3f8fd8",
  "#1fa187",
  "#8b6bd8",
  "#d05a9d",
  "#c98b2f",
  "#d66a3a",
  "#5b9f45",
  "#3fa6bd",
  "#b85cc7",
  "#b54b5c",
] as const;

function getSeatColor(
  playerOrder: readonly string[],
  playerId: string,
): string {
  const index = playerOrder.indexOf(playerId);
  return SEAT_COLORS[index >= 0 ? index % SEAT_COLORS.length : 0];
}

function getCubeRisk(cube: number): "low" | "mid" | "high" {
  const value = cube;
  if (value >= 26) {
    return "high";
  }
  if (value >= 15) {
    return "mid";
  }
  return "low";
}

function getPhaseLabel(state: MinusAuctionViewerState): string {
  if (state.phase === "AUCTION_DECISION") {
    return "타일 경매";
  }
  if (state.phase === "HIDDEN_AUCTION_BIDDING") {
    return "히든 입찰";
  }
  if (state.phase === "HIDDEN_AUCTION_DECISION") {
    return "히든 결정";
  }
  return "종료";
}

function formatNumber(value: number): string {
  return `${value}`;
}

function formatPrivateNumber(value: number | null | undefined): string {
  return typeof value === "number" ? formatNumber(value) : "?";
}
